import { type Dispatch, useEffect, useLayoutEffect, useRef, useState } from 'react'

import i18n from 'commons/libs/i18n'
import { type DefaultCustomerType, Info, type OptionProps, Search, Select } from 'components'
import { ROUTES } from 'constants/urls'
import type { Cliente } from 'container/CreateOrder'
import { Controller, useFormContext } from 'react-hook-form'
import { useMatch } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getCustomers } from 'services/client/list'
import { getClientAddress } from 'services/salesOrder'
import { getIncoterm } from 'services/shared'
import type { Incoterm } from 'services/shared/incoterm/types'

import * as S from '../styles'

const MAX_LIST_SIZE = 10

interface CustomerSelectorProps {
  disabled?: boolean
  clearNumber: number
  setClearNumber: Dispatch<React.SetStateAction<number>>
  setAddressOptions: Dispatch<React.SetStateAction<OptionProps[]>>
  defaultCustomer?: DefaultCustomerType
}

export const CustomerSelector = ({
  disabled,
  clearNumber,
  setClearNumber,
  setAddressOptions,
  defaultCustomer,
}: CustomerSelectorProps) => {
  const isCreating = useMatch(ROUTES.orders.getLink('internationalSampleOrder'))
  const { getValues, setValue, control } = useFormContext()

  const [baseKey, setBaseKey] = useState(0)
  const [searchWidth, setSearchWidth] = useState(0)
  const [searchPage, setSearchPage] = useState<number>(0)
  const [selectedClient, setSelectedClient] = useState({
    cd_clie: '',
    nm_clie: '',
  })
  const [incotermsData, setIncotermsData] = useState<Incoterm[]>([])

  const searchClientRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    setSearchWidth(searchClientRef?.current?.offsetWidth || 0)
  }, [searchClientRef?.current?.offsetWidth])

  const getDefaultInput = (selectedValue: 'cd_clie' | 'nm_clie') => {
    if (selectedClient && selectedClient[selectedValue] && selectedClient[selectedValue] !== '') {
      return selectedClient[selectedValue]
    }
    if (!isCreating && defaultCustomer && defaultCustomer[selectedValue] !== '') {
      return defaultCustomer[selectedValue]
    }

    return ''
  }

  useEffect(() => {
    if (!isCreating && defaultCustomer?.cd_clie) {
      const { cd_canl_dstb, cd_clie, cd_empr, cd_orgn_vend, cd_seto_atvd } = defaultCustomer

      getClientAddress({
        cd_canl_dstb,
        cd_clie,
        cd_empr,
        cd_orgn_vend,
        cd_seto_atvd,
      }).then((response) => {
        setAddressOptions(
          response.map((add) => ({
            title: add.ds_endr,
            value: add.cd_pcro,
          })),
        )
      })
    }
  }, [])

  const handleClientSelect = async (e: Cliente) => {
    setSelectedClient({
      cd_clie: e.cd_clie,
      nm_clie: e.nm_clie,
    })
    setValue('cd_clie', e)
    setValue('address', '')
    setValue('cd_escr_vend', e.cd_escr_vend)

    const { cd_empr, cd_orgn_vend, cd_canl_dstb, cd_seto_atvd, cd_clie } = e

    await getClientAddress({
      cd_canl_dstb,
      cd_clie,
      cd_empr,
      cd_orgn_vend,
      cd_seto_atvd,
    }).then((response) => {
      setAddressOptions(
        response?.map((add) => ({
          title: add.ds_endr,
          value: add.cd_pcro,
        })),
      )
      if (response.length === 1) {
        setValue('address', response[0].cd_pcro)
      }
    })
  }

  const clearClient = () => {
    setValue('cd_clie', {})
    setValue('address', '')
    setValue('cd_escr_vend', '')
    setAddressOptions([])
  }

  useEffect(() => {
    if (clearNumber) {
      clearClient()
      setTimeout(() => setClearNumber(0), 500)
    }
  }, [clearNumber])

  const getListSAPAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      cd_canl_dstb: getValues('cd_canl_dstb'),
      cd_clie: inputValue,
      cd_regi_vend: getValues('cd_regi_vend'),
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nr_pagi: page,
      qt_rowcount: MAX_LIST_SIZE,
    })

    return returnApi
  }

  const getListAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      cd_canl_dstb: getValues('cd_canl_dstb'),
      cd_regi_vend: getValues('cd_regi_vend'),
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nm_pcro: inputValue,
      nr_pagi: page,
      qt_rowcount: MAX_LIST_SIZE,
    })

    return returnApi
  }

  const fetchIncoterm = async () => {
    try {
      const response = await getIncoterm()

      setIncotermsData(response.data)
    } catch {
      toast.error(i18n.t('common.errors.unableToFetchOptions'))
    }
  }

  useEffect(() => {
    fetchIncoterm()
  }, [])

  const renderSearch = (
    key: string,
    label: string,
    id: string,
    defaultInputValue: 'cd_clie' | 'nm_clie',
    placeHolder: string,
    loadOptions: ((inputValue: string, page: string) => Promise<Cliente[]>) | undefined,
    infoText: React.ReactNode,
    isRightSided?: boolean,
  ) => (
    <Search<Cliente>
      clearInput={clearNumber}
      columnsSizeOption={['100%']}
      defaultInputValue={getDefaultInput(`${defaultInputValue}`)}
      disabled={disabled}
      flexWrapOptionDescription
      hideIcon
      id={id}
      isClientSearch
      isRightSided={isRightSided}
      key={`${key}-${baseKey}`}
      keysColumnsOption={['nm_clie']}
      label={label}
      labelAdornment={<Info iconSize={12}>{infoText}</Info>}
      labelsConcat={[defaultInputValue]}
      listWidth={`${searchWidth}px`}
      loadOnFocus
      loadOptions={loadOptions}
      minimumInput={0}
      optionsLimit={MAX_LIST_SIZE}
      optionsMaxHeight="400px"
      page={searchPage}
      placeHolder={placeHolder}
      setOptionSelected={(e) => {
        setBaseKey(baseKey + 1)
        handleClientSelect(e)
      }}
      setPage={setSearchPage}
      type="select"
      valueCase="upper"
    />
  )

  return (
    <div className="div-customer-wrapper" ref={searchClientRef}>
      <S.FieldWrapper className="div-client-sap">
        {renderSearch(
          'sap-key',
          i18n.t('components.orderHeader.sapCode'),
          'cod_sap',
          'cd_clie',
          'ˍ ˍ ˍ ˍ ˍ ˍ ˍ',
          getListSAPAsync,
          <>
            {i18n.t('components.orderHeader.sapKeySelectTooltip')} <br /> <br />
            {i18n.t('components.orderHeader.clientSelectTooltipComplement')}
          </>,
        )}
      </S.FieldWrapper>
      <S.FieldWrapper className="div-incoterm">
        <Controller
          control={control}
          name="cd_inco"
          render={({ field: { name, value, onChange } }) => (
            <Select
              disabled={disabled}
              focused={false}
              id="cd_inco"
              label={i18n.t('components.orderHeader.incoterm')}
              menuText={i18n.t('common.placeholders.clickToSelect')}
              name={name}
              onChange={onChange}
              options={incotermsData.map((term) => ({
                title: `${term.id} - ${term.description}`,
                value: term.id,
              }))}
              value={value ?? ''}
            />
          )}
        />
      </S.FieldWrapper>
      <S.FieldWrapper className="div-client-name">
        {renderSearch(
          'client-key',
          i18n.t('components.orderHeader.bussinessName'),
          'client',
          'nm_clie',
          i18n.t('common.placeholders.clickToWrite'),
          getListAsync,
          <>
            {i18n.t('components.orderHeader.bussinessNameSelectTooltip')} <br /> <br />
            {i18n.t('components.orderHeader.clientSelectTooltipComplement')}
          </>,
          true,
        )}
      </S.FieldWrapper>
    </div>
  )
}

export default CustomerSelector
